// encodeMosaic — composite the grid and encode it to an MP4 via WebCodecs.
//
// This is the B4 UPGRADE over web/mosaic.js's single-threaded SD-served
// `h264-mp4-encoder`: now that the Mosaic runs in the webapp's SECURE CONTEXT
// (ADR-0008 amendment / ADR-0017), the browser's hardware-accelerated WebCodecs
// `VideoEncoder` (H.264 / "avc") is available. We use mediabunny's `CanvasSource`
// + `Mp4OutputFormat` as a thin, current muxer around `VideoEncoder` so we don't
// hand-roll AVCC/MP4 box writing (mediabunny is the maintained successor to
// mp4-muxer; both by the same author). Browser-only — guarded by
// `mosaicEncodeSupported()` so the UI degrades gracefully where WebCodecs is
// missing (e.g. Node/jsdom, or a browser without VideoEncoder).
//
// The deterministic plan (grid size, frame count, per-cell timeline) is computed
// purely in mosaic.ts and unit-tested; this file is the canvas/codec glue, left to
// a manual/browser check (it cannot run under jsdom — no VideoEncoder there).

import {
  CanvasSource,
  Mp4OutputFormat,
  Output,
  BufferTarget,
  QUALITY_VERY_HIGH,
  canEncodeVideo,
} from "mediabunny";
import { cellPosition, planMosaic, type MosaicLayout, type MosaicPlan } from "./mosaic";
import type { MosaicCell } from "./mosaicCell";

/** Progress callback: `(percent 0..100, phase)` — `nullptr`-style optional. */
export type MosaicProgress = (percent: number, phase: "encoding" | "finalizing") => void;

export interface EncodeMosaicOptions {
  readonly layout: MosaicLayout;
  /** Explicit grid column count (the UI's stepper); overrides the layout heuristic. */
  readonly cols?: number;
  /** Whole loops of the base duration to play (the UI's length stepper; default 1). */
  readonly loops?: number;
  /** Progress reporter (default: none). */
  readonly onProgress?: MosaicProgress;
  /** Background fill behind cells (default black, like web/mosaic.js). */
  readonly background?: string;
  /** Abort a running encode (Cancel) — checked each output frame; rejects with an AbortError. */
  readonly signal?: AbortSignal;
}

/**
 * Is the WebCodecs H.264 encode path available in this environment?
 *
 * True only when `VideoEncoder` exists AND the browser reports it can encode H.264
 * ("avc") at our cell size. Returns false (no throw) under Node/jsdom or a browser
 * without WebCodecs, so the UI can show a clear "not supported here" message
 * instead of failing mid-encode.
 */
export async function mosaicEncodeSupported(): Promise<boolean> {
  if (typeof globalThis.VideoEncoder === "undefined") return false;
  try {
    // 240² is the smallest grid (1×1); if avc can't do that it can't do anything.
    return await canEncodeVideo("avc", { width: 240, height: 240 });
  } catch {
    return false;
  }
}

/**
 * Composite the Mosaic grid frame-by-frame and encode it to an MP4 Blob.
 *
 * Each output frame `t/fps` is painted by every cell's `drawCell` (a clip shows
 * its real-speed frame for that time; a photo is static), then handed to
 * mediabunny's `CanvasSource`, which encodes it as H.264 via WebCodecs and muxes
 * it into the MP4. `cells` are in pick order; the grid loops them (`i % count`) to
 * fill the last partial row, exactly like web/mosaic.js.
 *
 * @throws if WebCodecs is unavailable — call `mosaicEncodeSupported()` first.
 * @returns the finished MP4 as a `video/mp4` Blob.
 */
export async function encodeMosaic(
  cells: readonly MosaicCell[],
  options: EncodeMosaicOptions,
): Promise<Blob> {
  if (cells.length === 0) throw new Error("no cells to tile");
  if (typeof globalThis.VideoEncoder === "undefined") {
    throw new Error("WebCodecs VideoEncoder is unavailable in this browser");
  }

  const plan: MosaicPlan = planMosaic(
    cells.map((c) => c.duration),
    options.layout,
    undefined, // default fps
    options.cols,
    options.loops,
  );
  const { grid, frameCount, fps } = plan;
  const background = options.background ?? "#000";
  const count = cells.length;

  // Render at SCALE× with nearest-neighbor so each source pixel becomes a SCALE×SCALE output block.
  // That aligns the pixel-art with H.264's 4:2:0 chroma sampling (one chroma sample per 2×2): a
  // saturated colour (magenta/pink/red) is then sampled from a SOLID block instead of averaged across
  // a 1-px edge and washed out. Capped (~2160px) so a big grid doesn't blow up the encode time/size.
  const SCALE = Math.max(grid.width, grid.height) * 2 <= 2160 ? 2 : 1;
  const canvas = document.createElement("canvas");
  canvas.width = grid.width * SCALE;
  canvas.height = grid.height * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  if (SCALE !== 1) {
    ctx.imageSmoothingEnabled = false; // crisp pixels — each upscaled block is one solid colour
    ctx.scale(SCALE, SCALE); // draw in logical (grid) coords; the transform upscales the output
  }

  const output = new Output({
    format: new Mp4OutputFormat(),
    target: new BufferTarget(),
  });
  // VERY_HIGH bitrate: the cells are saturated, hard-edged pixel art — the worst case for H.264's
  // 4:2:0 chroma subsampling (vibrant magenta/pink bleeds + quantizes). A high bitrate keeps far
  // more of that chroma. (latencyMode already defaults to 'quality'.) The remaining loss is 4:2:0
  // itself — only a 4:4:4 codec (VP9/AV1) avoids it, at a playback-compatibility cost.
  const source = new CanvasSource(canvas, { codec: "avc", bitrate: QUALITY_VERY_HIGH });
  output.addVideoTrack(source, { frameRate: fps });
  await output.start();

  const frameDuration = 1 / fps;
  for (let f = 0; f < frameCount; f++) {
    if (options.signal?.aborted) {
      // Cancel the in-progress encode (best effort — mediabunny may expose cancel()), then bail.
      const cancel = (output as { cancel?: () => unknown }).cancel;
      if (cancel) {
        try {
          await cancel.call(output);
        } catch {
          /* best effort */
        }
      }
      throw new DOMException("Mosaic encode aborted", "AbortError");
    }
    const t = f / fps;
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, grid.width, grid.height);
    for (let i = 0; i < grid.cells; i++) {
      const { x, y } = cellPosition(i, grid.cols);
      cells[i % count]!.drawCell(ctx, x, y, /* size */ 240, t);
    }
    await source.add(t, frameDuration);
    options.onProgress?.(Math.round(((f + 1) / frameCount) * 100), "encoding");
  }

  options.onProgress?.(100, "finalizing");
  await output.finalize();
  const buffer = (output.target as BufferTarget).buffer;
  if (!buffer) throw new Error("encoder produced no output");
  return new Blob([buffer], { type: "video/mp4" });
}
